import { Menu, MenuProps, MenuRef } from 'antd';
import React, {
  Dispatch,
  ReactNode,
  Ref,
  SetStateAction,
  forwardRef,
  memo,
  useCallback,
  useMemo,
} from 'react';
import './PopupContextMenu.css';

export type ItemType<T> = Omit<MenuProps['items'][0], 'disabled'> & {
  label: string;
  icon?: ReactNode;
  key: string | number;
  onClick?: (item: T) => void;
  disabled?: boolean | undefined | ((record: T) => boolean);
  children?: ItemType<T>[];
};

export type ContextMenuState<T> = {
  currentRecord: T | null;
  // selectedRecords: T[];
  visible: boolean;
  x: number;
  y: number;
};

export interface MenuPopup<T> {
  items?: ItemType<T>[];
  onClick?: (menuItem?: ItemType<T>, record?: T) => void;
}

export interface ContextMenuProps<T> {
  // menu?: MenuPopup<T>;
  // items?: ItemType<T>[];
  triggerSubMenuAction?: 'hover' | 'click';
  setContextMenuState: Dispatch<SetStateAction<ContextMenuState<T>>>;
  onClick?: (menuItem?: ItemType<T>, record?: T) => void;
  setSelectedRow: Dispatch<SetStateAction<T[]>>;
}

export type PopupContextMenuProps<T> = ContextMenuProps<T> &
  ContextMenuState<T> & { menu: MenuPopup<T> };

const PopupContextMenu = <T,>(
  {
    menu,
    triggerSubMenuAction = 'hover',
    visible,
    x,
    y,
    currentRecord,
    setContextMenuState,
    setSelectedRow,
  }: PopupContextMenuProps<T>,
  ref: Ref<MenuRef>
) => {
  const { items, onClick } = menu || {};
  // We need to remove onCLick and disable function from item because:
  // the ant menu have a internal onclick trigger, and the disabled will be consider as true

  const removeOnClickEvent = useCallback(
    (item: ItemType<T>) => {
      const { onClick, ...newItem } = item;
      const isDisabled =
        typeof item.disabled === 'function' ? item.disabled(currentRecord) : item.disabled;

      if (isDisabled) return null;

      if (newItem.children) {
        newItem.children = newItem.children
          .map(removeOnClickEvent)
          .filter((it): it is ItemType<T> => it?.disabled === false);
      }

      return { ...newItem, disabled: !!isDisabled } as ItemType<T>;
    },
    [currentRecord]
  );

  const menuItems: ItemType<T>[] = useMemo(
    () =>
      (items || [])
        .map(removeOnClickEvent)
        .filter((it): it is ItemType<T> => it?.disabled === false),
    [items, removeOnClickEvent]
  );

  if (!menu) return null;

  const handleClick = ({ keyPath }: { keyPath: string[] }) => {
    const getSelectedItem = (items: ItemType<T>[], index: number): ItemType<T> | null => {
      // KeyPath will be string even we pass key as number
      const currentSelect = items.find((it) => String(it.key) === keyPath[index]);
      if (!currentSelect) return null;
      if (index === 0) return currentSelect;
      return getSelectedItem(currentSelect.children, index - 1);
    };
    const selectedItem = getSelectedItem(items || [], keyPath.length - 1);
    if (selectedItem && typeof onClick === 'function') onClick(selectedItem);
  };

  const boxHeight = 32 * items.length + 8;
  const boxWidth = 200;
  let boxStyle: any = { zIndex: 2 };

  if (boxHeight + y > window.innerHeight) {
    boxStyle = {
      ...boxStyle,
      bottom: `${window.innerHeight - y}px`,
      top: 'unset',
    };
  } else {
    boxStyle = { ...boxStyle, top: `${y}px` };
  }

  if (boxWidth + x > window.innerWidth) {
    boxStyle = {
      ...boxStyle,
      right: `${window.innerWidth - x}px`,
      left: 'unset',
    };
  } else {
    boxStyle = { ...boxStyle, left: `${x}px` };
  }

  return (
    <div style={{ userSelect: 'none', touchAction: 'none' }}>
      {visible &&
        items?.some(
          (it) =>
            !(typeof it.disabled === 'function' && it.disabled(currentRecord)) &&
            it.disabled !== true
        ) && (
          <>
            <div
              className="overlay-bg"
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                opacity: 0.5,
                zIndex: 1,
                userSelect: 'none',
                touchAction: 'none',
              }}
              onMouseDown={() => (
                setSelectedRow?.([]), setContextMenuState((prev) => ({ ...prev, visible: false }))
              )}
            />
            <Menu
              ref={ref}
              className="popup border-background-light-5 bg-background-light-5 text-black dark:border-background-dark-5 dark:bg-background-dark-5 dark:text-white"
              items={menuItems as unknown as MenuProps['items']}
              mode="vertical"
              style={boxStyle}
              triggerSubMenuAction={triggerSubMenuAction}
              onClick={handleClick}
            />
          </>
        )}
    </div>
  );
};

const ForwardedPopupContextMenu = forwardRef(PopupContextMenu) as <T>(
  props: PopupContextMenuProps<T> & { ref?: React.Ref<MenuRef> }
) => ReturnType<typeof PopupContextMenu>;

export default memo(ForwardedPopupContextMenu) as typeof ForwardedPopupContextMenu;
